import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  Calendar, Video, Clock, Users, ExternalLink, 
  ChevronLeft, ChevronRight, Star, CalendarPlus, Copy, Check, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsCalendarPage = () => {
  const { isFoundingMember, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);

  useEffect(() => {
    if (isFoundingMember && token) {
      fetchSessions();
      fetchCalendarToken();
    } else if (!isFoundingMember) {
      setLoading(false);
    }
  }, [isFoundingMember, token]);

  const fetchSessions = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API_URL}/calendar/sessions`, {
        headers
      });
      setUpcomingSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      // Fallback to empty array
      setUpcomingSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarToken = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API_URL}/calendar/token`, {
        headers
      });
      const calToken = response.data.token;
      // Construct the ICS feed URL
      const feedUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/feed.ics?token=${calToken}`;
      setCalendarUrl(feedUrl);
    } catch (error) {
      console.error('Failed to get calendar token:', error);
    }
  };

  const handleCopyCalendarUrl = async () => {
    try {
      await navigator.clipboard.writeText(calendarUrl);
      setCopied(true);
      toast.success('Calendar link copied! Paste it into your calendar app.');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleSubscribeCalendar = (type) => {
    if (!calendarUrl) {
      toast.error('Calendar link not available');
      return;
    }

    // Convert https to webcal for calendar subscription
    const webcalUrl = calendarUrl.replace('https://', 'webcal://').replace('http://', 'webcal://');

    if (type === 'apple' || type === 'outlook') {
      // Direct subscription via webcal protocol
      window.location.href = webcalUrl;
      toast.success('Opening calendar subscription...');
    } else if (type === 'google') {
      // Google Calendar requires a different approach - add by URL
      const googleUrl = `https://calendar.google.com/calendar/r/settings/addbyurl?url=${encodeURIComponent(calendarUrl)}`;
      window.open(googleUrl, '_blank');
      toast.success('Opening Google Calendar...');
    }
    setShowCalendarOptions(false);
  };

  const handleJoinSession = (session) => {
    if (session.zoomLink) {
      window.open(session.zoomLink, '_blank');
      toast.success(`Joining: ${session.title}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isFoundingMember) {
    return (
      <div className="space-y-6" data-testid="events-locked">
        <div className="card-cosmic p-12 text-center">
          <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-900 mb-2">Founding Member Exclusive</h2>
          <p className="text-stone-600 mb-6">Upgrade to access live sessions with Heather and the community</p>
          <Button onClick={() => navigate('/pricing')} className="btn-cosmic">
            Become a Founding Member
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="events-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-stone-900">Live Sessions Calendar</h1>
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <Star className="w-3 h-3" /> Founding Member
            </span>
          </div>
          <p className="text-stone-600">Two live online sessions a month with Heather and the community</p>
        </div>
        
        {/* Calendar Subscribe Button */}
        {calendarUrl && (
          <div className="relative">
            <Button 
              onClick={() => setShowCalendarOptions(!showCalendarOptions)}
              variant="outline"
              className="flex items-center gap-2 border-primary/30 hover:border-primary hover:bg-primary/5"
              data-testid="subscribe-calendar-btn"
            >
              <CalendarPlus className="w-4 h-4 text-primary" />
              Subscribe to Calendar
            </Button>
            
            {/* Calendar Options Dropdown */}
            {showCalendarOptions && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-stone-200 p-2 z-50">
                <button
                  onClick={() => handleSubscribeCalendar('apple')}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 flex items-center gap-3 transition-colors"
                  data-testid="subscribe-apple-btn"
                >
                  <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-stone-600" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">Apple Calendar</p>
                    <p className="text-xs text-stone-500">iPhone, iPad, Mac</p>
                  </div>
                </button>
                
                <button
                  onClick={() => handleSubscribeCalendar('google')}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 flex items-center gap-3 transition-colors"
                  data-testid="subscribe-google-btn"
                >
                  <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-stone-600" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">Google Calendar</p>
                    <p className="text-xs text-stone-500">Gmail, Android</p>
                  </div>
                </button>
                
                <button
                  onClick={() => handleSubscribeCalendar('outlook')}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 flex items-center gap-3 transition-colors"
                  data-testid="subscribe-outlook-btn"
                >
                  <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-stone-600" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">Outlook Calendar</p>
                    <p className="text-xs text-stone-500">Microsoft 365</p>
                  </div>
                </button>
                
                <div className="border-t border-stone-200 mt-2 pt-2">
                  <button
                    onClick={handleCopyCalendarUrl}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 flex items-center gap-3 transition-colors"
                    data-testid="copy-calendar-link-btn"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-primary" />}
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">{copied ? 'Copied!' : 'Copy Link'}</p>
                      <p className="text-xs text-stone-500">For other calendar apps</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Next Session Highlight */}
      {upcomingSessions.length > 0 && (
        <div className="card-cosmic p-6 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="flex items-center gap-2 text-primary mb-3">
            <Video className="w-5 h-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">Next Session</span>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2">{upcomingSessions[0].title}</h2>
              <p className="text-stone-600 mb-4">{upcomingSessions[0].description}</p>
              
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-stone-700">
                  <Calendar className="w-4 h-4 text-primary" />
                  {upcomingSessions[0].date}
                </div>
                <div className="flex items-center gap-2 text-stone-700">
                  <Clock className="w-4 h-4 text-primary" />
                  {upcomingSessions[0].time}
                </div>
                <div className="flex items-center gap-2 text-stone-700">
                  <Users className="w-4 h-4 text-primary" />
                  {upcomingSessions[0].duration}
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => handleJoinSession(upcomingSessions[0])}
              className="btn-cosmic whitespace-nowrap"
              data-testid="join-next-session-btn"
            >
              <Video className="w-4 h-4 mr-2" />
              Join on Zoom
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* All Upcoming Sessions */}
      <div>
        <h2 className="text-xl font-bold text-stone-900 mb-4">Upcoming Sessions</h2>
        <div className="space-y-4">
          {upcomingSessions.map((session, index) => (
            <div 
              key={session.id}
              className={`card-cosmic p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${index === 0 ? 'opacity-50' : ''}`}
              data-testid={`session-card-${session.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  session.type === 'workshop' 
                    ? 'bg-accent/10 text-accent' 
                    : 'bg-primary/10 text-primary'
                }`}>
                  {session.type === 'workshop' ? (
                    <Star className="w-6 h-6" />
                  ) : (
                    <Users className="w-6 h-6" />
                  )}
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-stone-900">{session.title}</h3>
                    {session.type === 'workshop' && (
                      <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">Workshop</span>
                    )}
                  </div>
                  <p className="text-sm text-stone-600 mb-2 line-clamp-1">{session.description}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-stone-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {session.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {session.time}
                    </span>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={() => handleJoinSession(session)}
                variant="outline"
                className="flex items-center gap-2 whitespace-nowrap"
                data-testid={`join-session-btn-${session.id}`}
              >
                <Video className="w-4 h-4" />
                Join
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="card-cosmic p-6 bg-stone-50">
        <h3 className="font-semibold text-stone-900 mb-2">How It Works</h3>
        <ul className="space-y-2 text-sm text-stone-600">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Sessions are held twice a month via Zoom
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Click "Join on Zoom" when the session is about to start
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Can't make it live? Recordings are shared with members
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Have questions? Bring them to the session!
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EventsCalendarPage;
